import { useIntl } from 'react-intl'
import { colors, colors as oldColors } from 'shared/assets/styles'
import { Layout } from '../../components'
import React, { useCallback, useEffect, useState } from 'react'
import { isMobile, isTablet } from 'react-device-detect'
import { useHistory } from 'react-router-dom'
import { H2 } from '../../../../common/typography'
import { ResponsiveContainer } from '../../../../assets/styles'
import styled from 'styled-components'
import { BagOnClick } from '../../../../assets/icons'
import { ChevaletIcon } from '../../../../../app-v2/assets/icons'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { featureCodes } from 'shared/utils'
import { parseKingTableUrl } from '../../components/choose-order-type/utils'
import { QRCodeScannerModal } from '../../../../common/qr-code-scanner/QRCodeScannerModal'
import { ButtonIcon } from './components/ButtonIcon'
import { COLOR, SPACING } from '../../../../../app-v2/design-tokens'

export const OrderTypeChoice = () => {
    const intl = useIntl()
    const history = useHistory()

    const tableOrderEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.TABLE_ORDER)
    )
    const clickAndCollectEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.TABLE_ORDER)
    )
    const newOrderFlowEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.ENABLE_NEW_ORDER_FLOW)
    )

    useEffect(() => {
        if (!isMobile && !isTablet) {
            if (newOrderFlowEnabled) {
                document.location.href = intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            } else {
                history.push(
                    intl.formatMessage({
                        id: 'routes.clickandcollect.restaurant.choice'
                    })
                )
            }
        }
    }, [history, intl, newOrderFlowEnabled])

    const goToCC = useCallback(() => {
        if (newOrderFlowEnabled) {
            document.location.href = intl.formatMessage({
                id: 'routes.clickandcollect.restaurant.choice'
            })
        } else {
            history.push(
                intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            )
        }
    }, [history, intl, newOrderFlowEnabled])

    const goToKingTable = useCallback(
        (params: {
            restaurant: string,
            table: string,
            terrace: string,
            token: string
        }) => {
            const urlParam = new URLSearchParams()
            urlParam.set('restaurant', params.restaurant)
            urlParam.set('table', params.table)
            urlParam.set('terrace', params.terrace)
            urlParam.set('token', params.token)

            if (newOrderFlowEnabled) {
                document.location.href =
                    intl.formatMessage({
                        id: 'routes.kingtable.full'
                    }) + `?${urlParam.toString()}`
            } else {
                history.push(
                    intl.formatMessage({
                        id: 'routes.kingtable.full'
                    }) + `?${urlParam.toString()}`
                )
            }
        },
        [history, intl, newOrderFlowEnabled]
    )

    const goToNoDesktop = useCallback(() => {
        if (newOrderFlowEnabled) {
            document.location.href = intl.formatMessage({
                id: 'routes.kingtable.no-desktop'
            })
        } else {
            history.push(
                intl.formatMessage({
                    id: 'routes.kingtable.no-desktop'
                })
            )
        }
    }, [history, intl])

    const [scannerOpen, setScannerOpen] = useState(false)

    return (
        <Layout
            background="plain"
            border
            showLogoBK
            closeEnabled
            backgroundColor={oldColors.tertiary}>
            <StyledContainer>
                <H2 center>
                    {intl.formatMessage({
                        id: 'ecommerce.order.mode.choice'
                    })}
                </H2>
                <StyledButtonContainer>
                    <ButtonIcon
                        onClick={goToCC}
                        diabled={!clickAndCollectEnabled}
                        icon={
                            <BagOnClick
                                color={
                                    clickAndCollectEnabled
                                        ? COLOR.PRIMARY
                                        : colors.disabled
                                }
                                size={24}
                            />
                        }
                        title={intl.formatMessage({
                            id: 'clickandcollect'
                        })}
                        description={intl.formatMessage({
                            id:
                                'ecommerce.order.mode.choice.clickandcollect.description'
                        })}
                    />
                    {(isMobile || isTablet) && (
                        <ButtonIcon
                            onClick={() => {
                                if (isMobile || isTablet) {
                                    setScannerOpen(true)
                                } else {
                                    goToNoDesktop()
                                }
                            }}
                            diabled={!tableOrderEnabled}
                            icon={
                                <ChevaletIcon
                                    color={
                                        tableOrderEnabled
                                            ? COLOR.PRIMARY
                                            : colors.disabled
                                    }
                                    size={24}
                                />
                            }
                            title={intl.formatMessage({
                                id: 'kingtable'
                            })}
                            description={intl.formatMessage({
                                id:
                                    'ecommerce.order.mode.choice.kingtable.description'
                            })}
                        />
                    )}
                </StyledButtonContainer>
            </StyledContainer>
            <QRCodeScannerModal
                helpText={intl.formatMessage({
                    id: 'ecommerce.kingtable.scan.description'
                })}
                open={scannerOpen}
                idleColor={COLOR.PRIMARY}
                onClose={() => {
                    setScannerOpen(false)
                }}
                onScan={result => {
                    const parsed = parseKingTableUrl(result)

                    if (!parsed) {
                        throw new Error(
                            intl.formatMessage({
                                id: 'qr.code.table.error'
                            })
                        )
                    }

                    goToKingTable(parsed)
                }}
            />
        </Layout>
    )
}

const StyledContainer = styled(ResponsiveContainer)`
    margin: 90px auto 0;
    min-height: calc(100vh - 90px);
`

const StyledButtonContainer = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    padding: ${SPACING.L} ${SPACING.XS} ${SPACING.XS};
    flex-direction: column;
    gap: ${SPACING.XS};
`
